import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Popover, {
  type PopoverMenuTouchableOptionProps as Option,
} from 'modules/core/components/PopoverMenu'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type Employee from 'modules/employees/store/models/Employee'
import { employeeJob } from 'modules/job_catalog/store/collections/roles'
import type Membership from 'modules/teams/store/models/Membership'

interface Props {
  employee: Employee
  membership: Membership
  canManageTeams: boolean
  contractVersions: ContractVersions
}

const Leader = ({ employee, membership, canManageTeams, contractVersions }: Props) => {
  const job = employeeJob(contractVersions, employee)
  const { navigation } = useNavigation()

  const onClickMemberDemote = () => {
    asyncAction(() =>
      membership.save({
        lead: false,
      })
    )
  }

  const onClickMemberRemove = () => {
    asyncAction(() => membership.destroy())
  }

  const options: Option[] = [
    {
      title: i18n.t('teams.change_to_member'),
      onClick: onClickMemberDemote,
    },
    {
      title: i18n.t('teams.leaders.remove_title'),
      onClick: onClickMemberRemove,
      danger: true,
    },
  ]

  return (
    <Box
      padding="s12"
      border={{
        all: {
          style: 'solid',
          width: 's1',
          color: 'grey400',
        },
      }}
      style={{ maxWidth: '100%' }}
      borderRadius={{
        all: 'abs008',
      }}
    >
      <Box width="full" gap="s16" flexDirection="row" justifyContent="spaceBetween">
        <Box justifyContent="flexStart" flexDirection="row" alignItems="center">
          <UserAvatar user={employeeUser(employee)} size="s40" />
          <Box paddingLeft="s24" alignItems="flexStart">
            <Text.Normal weight="medium" color="grey1000">
              {employeeUser(employee).fullName()}
            </Text.Normal>
            <Text.Normal size="200" color="grey800" weight="medium">
              {job}
            </Text.Normal>
          </Box>
        </Box>
        <Box alignItems="center" justifyContent="flexEnd" flexDirection="row" gap="s12">
          {canManageTeams && (
            <Popover type="list">
              <Popover.Trigger>
                <Button.Decorative type="secondary" iconName="ellipsis" />
              </Popover.Trigger>
              <Popover.Content side="bottom" align="center">
                {options.map(({ title, description, onClick, danger }, index) => (
                  <Popover.TouchableOption
                    key={index}
                    danger={danger}
                    title={title}
                    onClick={onClick}
                    description={description}
                  />
                ))}
              </Popover.Content>
            </Popover>
          )}
          <Button.Router
            to={navigation.employees.id.fullPath({ id: employee.id })}
            type="secondary"
            iconName="arrow-right"
          />
        </Box>
      </Box>
    </Box>
  )
}

export default observer(Leader)
